import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  CalendarIcon, Clock, Users, Zap, Star, Loader2, Gamepad2, Check,
  MessageSquare, ChevronDown, Mail, User as UserIcon, Phone, Sparkles, DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { isEmbedded, openCheckout, getEmbedReturnUrls } from "@/lib/embed";
import { DEFAULT_TZ, tzLabel, zonedWallToUtc, nowInTz } from "@/lib/timezones";
import { useAuth } from "@/context/AuthContext";
import { SectionCard, InlineLoader } from "@/components/common";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const SLOTS = [];
for (let m = 9 * 60; m <= 22 * 60; m += 30) SLOTS.push(m); // 9:00 AM – 10:00 PM in 30-min steps
const DURATIONS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4];
const PARTY_DURATIONS = [1, 2, 3, 4, 5, 6, 7, 8];
const STATION_ICONS = {
  racing_sim: "🏎️",
  flight_sim: "✈️",
  vr_station: "🥽",
  gaming_station: "🎮",
  golf_sim: "⛳",
};
const fmtSlot = (m) => {
  const mm = ((m % 1440) + 1440) % 1440;
  const h = Math.floor(mm / 60);
  const min = mm % 60;
  const hr12 = ((h + 11) % 12) + 1;
  return `${hr12}:${min === 0 ? "00" : min} ${h < 12 ? "AM" : "PM"}`;
};
const fmtDuration = (d) => (d < 1 ? `${Math.round(d * 60)} min` : `${d} hour${d > 1 ? "s" : ""}`);

const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-400">{label}</span>
    <span className="font-semibold" style={{ color: accent || "#e2e8f0" }}>{value}</span>
  </div>
);

/**
 * Shared booking experience used by both the logged-in app (mode="member")
 * and the public no-login widget (mode="guest").
 */
export const BookingTool = ({ mode = "member" }) => {
  const isGuest = mode === "guest";
  const { user, refreshUser } = useAuth();

  const [date, setDate] = useState(new Date());
  const [startMinutes, setStartMinutes] = useState(18 * 60);
  const [duration, setDuration] = useState(1);
  const [stations, setStations] = useState([]);
  const [availableIds, setAvailableIds] = useState(new Set());
  const [selected, setSelected] = useState([]);
  const [partySize, setPartySize] = useState(1);
  const [specialRequests, setSpecialRequests] = useState("");
  const [motionByStation, setMotionByStation] = useState(() => new Set());
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // member-only
  const [pointsToUse, setPointsToUse] = useState(0);
  const [useFreeCredit, setUseFreeCredit] = useState(false);
  const [membershipDiscount, setMembershipDiscount] = useState(0);

  // guest-only
  const [guest, setGuest] = useState({ guest_name: "", guest_email: "", guest_phone: "" });

  // Scheduling: weekly hours + holiday closures (lounge local = Atlantic)
  const [businessHours, setBusinessHours] = useState(null);
  const [closures, setClosures] = useState([]);
  // Multi-unit discount tiers + party (whole-lounge) config
  const [multiUnitTiers, setMultiUnitTiers] = useState([]);
  const [partyConfig, setPartyConfig] = useState(null);
  const [partyBooking, setPartyBooking] = useState(false);
  const [taxSettings, setTaxSettings] = useState({ enabled: false, rate_percent: 0, label: "HST" });

  // Booking timezone: member uses saved profile preference; guests default to Atlantic (Halifax).
  const tz = DEFAULT_TZ;

  const dateStr = format(date, "yyyy-MM-dd");
  const weekday = date.getDay(); // 0=Sun..6=Sat
  const closure = closures.find((c) => c.date === dateStr);
  const dayHours = businessHours?.find((d) => d.weekday === weekday);
  const dayClosed = !!closure || !!dayHours?.is_closed;
  const openM = dayHours?.open_minutes ?? 9 * 60;
  const closeM = dayHours?.close_minutes ?? 22 * 60;

  const startTime = useMemo(
    () => zonedWallToUtc(date.getFullYear(), date.getMonth(), date.getDate(), startMinutes, tz),
    [date, startMinutes, tz]
  );
  const endTime = useMemo(() => {
    const d = new Date(startTime); d.setMinutes(d.getMinutes() + Math.round(duration * 60)); return d;
  }, [startTime, duration]);

  // Determine which start slots are in the past for the selected date (in the booking timezone).
  const now = nowInTz(tz);
  const isSelectedToday =
    date.getFullYear() === now.y && date.getMonth() === now.monthIndex && date.getDate() === now.day;
  const isPastSlot = (m) => isSelectedToday && m <= now.minutes;
  // A start slot is unavailable if the lounge is closed, it's in the past, before opening,
  // or the session would run past closing time.
  const slotState = (m) => {
    if (dayClosed) return "closed";
    if (isPastSlot(m)) return "past";
    if (m < openM) return "before";
    if (m + Math.round(duration * 60) > closeM) return "after";
    return "ok";
  };
  const isUnavailable = (m) => slotState(m) !== "ok";

  // Keep the selected start time on a valid slot (skips past / out-of-hours / closed).
  useEffect(() => {
    if (businessHours == null) return;
    if (isUnavailable(startMinutes)) {
      const next = SLOTS.find((m) => !isUnavailable(m));
      if (next != null && next !== startMinutes) setStartMinutes(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, tz, duration, businessHours, closures]);

  useEffect(() => {
    apiClient.get("/stations").then(({ data }) => setStations(data)).catch(() => {});
    apiClient.get("/business-hours").then(({ data }) => setBusinessHours(data)).catch(() => setBusinessHours([]));
    apiClient.get("/closures").then(({ data }) => setClosures(data)).catch(() => {});
    apiClient.get("/multi-unit-tiers").then(({ data }) => setMultiUnitTiers(Array.isArray(data) ? data : [])).catch(() => {});
    apiClient.get("/party-booking").then(({ data }) => setPartyConfig(data || null)).catch(() => {});
    apiClient.get("/tax-settings").then(({ data }) => data && setTaxSettings(data)).catch(() => {});
    if (!isGuest) {
      apiClient.get("/memberships/my").then(({ data }) => setMembershipDiscount(data?.plan?.discount_percentage || 0)).catch(() => {});
    }
  }, [isGuest]);

  // Party booking: auto-select all available stations & force full-hour durations.
  // Turning it OFF clears the selection so the user picks fresh.
  useEffect(() => {
    if (partyBooking) {
      const inc = Number(partyConfig?.increment_minutes || 60);
      if (inc >= 60 && Number.isFinite(duration) && !Number.isInteger(duration)) {
        setDuration(Math.max(Math.ceil(duration), Number(partyConfig?.min_hours || 1)));
      }
      const all = stations.filter((s) => availableIds.has(s.id)).map((s) => s.id);
      setSelected(all);
    } else {
      // Explicit un-toggle: clear all station selections
      setSelected([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyBooking, stations, availableIds]);

  useEffect(() => {
    let active = true;
    const check = async () => {
      setLoadingAvail(true);
      try {
        const { data } = await apiClient.get("/stations/availability", {
          params: { start_time: startTime.toISOString(), end_time: endTime.toISOString() },
        });
        if (active) {
          const ids = new Set(data.map((s) => s.id));
          setAvailableIds(ids);
          setSelected((prev) => prev.filter((id) => ids.has(id)));
        }
      } catch (e) { /* ignore */ } finally {
        if (active) setLoadingAvail(false);
      }
    };
    check();
    return () => { active = false; };
  }, [startTime, endTime]);

  const toggleStation = (id) => {
    if (partyBooking) return;   // Locked when booking the whole lounge
    if (!availableIds.has(id)) return;
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const selectedStations = stations.filter((s) => selected.includes(s.id));

  // Shared-asset awareness: partners of a selected station are normally blocked, EXCEPT when
  // selecting them would complete a whole-lounge booking (guest switches between them mid-session).
  const sharedPartnersOf = (s) => stations.filter((o) => o.id !== s.id && ((o.shared_with || []).includes(s.id) || (s.shared_with || []).includes(o.id)));
  const blockedPartnerIds = new Set();
  if (!partyBooking) {
    stations.forEach((s) => {
      if (!selected.includes(s.id) && sharedPartnersOf(s).some((o) => selected.includes(o.id))) blockedPartnerIds.add(s.id);
    });
  }
  const selectableUnselected = stations.filter((s) => !selected.includes(s.id) && !blockedPartnerIds.has(s.id));
  const completingLounge = blockedPartnerIds.size > 0 && selectableUnselected.length === 0;
  const effectiveBlockedIds = completingLounge ? new Set() : blockedPartnerIds;

  // Groups (2+ stations sharing components) present in the current selection, for the info note.
  const sharedGroupsSelected = (() => {
    const seen = new Set();
    const groups = [];
    selectedStations.forEach((s) => {
      if (seen.has(s.id)) return;
      const mates = sharedPartnersOf(s).filter((o) => selected.includes(o.id));
      if (mates.length) {
        const group = [s, ...mates];
        group.forEach((g) => seen.add(g.id));
        groups.push(group.map((g) => g.name));
      }
    });
    return groups;
  })();

  const stationsAmount = partyBooking
    ? Number(partyConfig?.hourly_rate || 0) * duration
    : selectedStations.reduce((sum, s) => sum + s.hourly_rate * duration, 0);
  const motionEligible = selectedStations.filter((s) => s.motion_available);
  const activeMotionIds = useMemo(
    () => selectedStations.filter((s) => s.motion_available && motionByStation.has(s.id)).map((s) => s.id),
    [selectedStations, motionByStation]
  );
  const motionAmount = selectedStations
    .filter((s) => s.motion_available && motionByStation.has(s.id))
    .reduce((sum, s) => sum + Number(s.motion_price || 0) * duration, 0);
  const preDiscountAmount = stationsAmount + motionAmount;
  // Multi-unit auto-discount (skipped for party since party already uses a flat rate)
  const multiUnitPct = useMemo(() => {
    if (partyBooking) return 0;
    let match = 0;
    for (const t of multiUnitTiers || []) {
      if (selectedStations.length >= Number(t.min_stations || 0)) match = Math.max(match, Number(t.discount_percentage || 0));
    }
    return match;
  }, [multiUnitTiers, selectedStations.length, partyBooking]);
  const multiUnitDiscount = preDiscountAmount * (multiUnitPct / 100);
  const baseAmount = preDiscountAmount - multiUnitDiscount;
  // Prune motion selections when a station is deselected or loses motion eligibility
  useEffect(() => {
    setMotionByStation((prev) => {
      const eligibleIds = new Set(selectedStations.filter((s) => s.motion_available).map((s) => s.id));
      const next = new Set();
      prev.forEach((id) => { if (eligibleIds.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [selectedStations]);
  const toggleMotion = (id) => {
    setMotionByStation((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // member pricing
  const afterMembership = baseAmount * (1 - membershipDiscount / 100);
  const pointsDiscount = pointsToUse / 100;
  const afterPoints = Math.max(0, afterMembership - pointsDiscount);
  const freeCreditValue = !isGuest && useFreeCredit && selectedStations.length && duration >= 1
    ? Math.min(afterPoints, selectedStations[0].hourly_rate * (1 - membershipDiscount / 100)) : 0;
  const memberTotal = Math.max(0, afterPoints - freeCreditValue);

  const estTotal = isGuest ? baseAmount : memberTotal;
  const taxRate = taxSettings.enabled ? Number(taxSettings.rate_percent || 0) : 0;
  const taxAmount = estTotal > 0 && taxRate > 0 ? Math.round(estTotal * taxRate) / 100 : 0;
  const grandTotal = Math.round((estTotal + taxAmount) * 100) / 100;
  const potentialPoints = Math.round(baseAmount); // 1 pt per $ at base tier

  const handleMemberBook = async () => {
    const { data: booking } = await apiClient.post("/bookings", {
      station_ids: selected, start_time: startTime.toISOString(), end_time: endTime.toISOString(),
      party_size: partySize, points_to_use: pointsToUse, use_free_session_credit: useFreeCredit,
      motion_selected: activeMotionIds.length > 0,
      motion_station_ids: activeMotionIds,
      special_requests: specialRequests || null,
      party_booking: partyBooking,
    });
    await refreshUser();
    const { data: checkout } = await apiClient.post(`/payments/checkout?booking_id=${booking.id}&client_origin=${encodeURIComponent(window.location.origin)}`);
    if (booking.total_amount <= 0) {
      toast.success("Booking confirmed! Paid with credits/points.");
      window.location.href = "/my-bookings";
      return;
    }
    if (checkout.url) {
      openCheckout(checkout.url);
      if (isEmbedded()) { toast.info("Complete payment in the new tab, then return here."); window.location.href = "/my-bookings"; }
    } else { window.location.href = "/my-bookings"; }
  };

  const handleGuestBook = async () => {
    if (!guest.guest_name.trim()) { toast.error("Please enter your name"); return false; }
    if (!guest.guest_email.trim() || !guest.guest_email.includes("@")) { toast.error("Please enter a valid email"); return false; }
    const { data: booking } = await apiClient.post("/public/bookings", {
      station_ids: selected, start_time: startTime.toISOString(), end_time: endTime.toISOString(),
      party_size: partySize, guest_name: guest.guest_name.trim(), guest_email: guest.guest_email.trim(),
      guest_phone: guest.guest_phone.trim() || null,
      motion_selected: activeMotionIds.length > 0,
      motion_station_ids: activeMotionIds,
      party_booking: partyBooking,
    });
    const { returnUrl, cancelUrl } = getEmbedReturnUrls();
    const qp = new URLSearchParams({
      booking_id: booking.id,
      client_origin: window.location.origin,
    });
    if (returnUrl) qp.set("return_url", returnUrl);
    if (cancelUrl) qp.set("cancel_return_url", cancelUrl);
    const { data: checkout } = await apiClient.post(`/public/payments/checkout?${qp.toString()}`);
    if (checkout.url) {
      openCheckout(checkout.url);
      if (isEmbedded()) toast.info("Complete payment in the new tab to confirm your booking.");
    } else {
      toast.success("Booking confirmed!");
      window.location.href = "/booking-success?guest=1";
    }
    return true;
  };

  const handleSubmit = async () => {
    if (selected.length === 0) { toast.error("Select at least one station"); return; }
    setSubmitting(true);
    try {
      if (isGuest) await handleGuestBook();
      else await handleMemberBook();
    } catch (err) {
      toast.error(getErrorMessage(err, "Booking failed"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* Guest details (guest mode only) */}
        {isGuest && (
          <SectionCard>
            <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
              <UserIcon className="w-5 h-5" style={{ color: "#00ff9d" }} /> Your Details
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Full Name *</label>
                <input className="input-cyber" placeholder="John Racer" value={guest.guest_name}
                  onChange={(e) => setGuest({ ...guest, guest_name: e.target.value })} data-testid="guest-name" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email *</label>
                <input type="email" className="input-cyber" placeholder="you@example.com" value={guest.guest_email}
                  onChange={(e) => setGuest({ ...guest, guest_email: e.target.value })} data-testid="guest-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Phone (optional)</label>
                <input className="input-cyber" placeholder="555 123 4567" value={guest.guest_phone}
                  onChange={(e) => setGuest({ ...guest, guest_phone: e.target.value })} data-testid="guest-phone" />
              </div>
            </div>
          </SectionCard>
        )}

        {/* Date & time */}
        <SectionCard>
          <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" style={{ color: "#00e5ff" }} /> Date & Time
          </h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="input-cyber flex items-center justify-between" data-testid="date-picker-trigger">
                    {format(date, "MMM d, yyyy")} <CalendarIcon className="w-4 h-4 text-slate-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 glass border-white/10" align="start">
                  <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Start Time</label>
              <div className="relative">
                <select className="input-cyber appearance-none pr-10" value={startMinutes}
                  onChange={(e) => setStartMinutes(Number(e.target.value))} data-testid="start-time-select">
                  {SLOTS.map((m) => {
                    const st = slotState(m);
                    const suffix = st === "past" ? " — past" : st === "closed" ? " — closed" : st === "before" || st === "after" ? " — off-hours" : "";
                    return <option key={m} value={m} disabled={st !== "ok"} className="bg-slate-900">{fmtSlot(m)}{suffix}</option>;
                  })}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Duration</label>
              <div className="relative">
                <select className="input-cyber appearance-none pr-10" value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))} data-testid="duration-select">
                  {(partyBooking ? PARTY_DURATIONS.filter((d) => d >= Number(partyConfig?.min_hours || 1)) : DURATIONS).map((d) => <option key={d} value={d} className="bg-slate-900">{fmtDuration(d)}</option>)}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>
          <p className="text-slate-400 text-sm mt-4 flex items-center gap-2" data-testid="booking-time-summary">
            <Clock className="w-4 h-4" /> {fmtSlot(startMinutes)} – {fmtSlot(startMinutes + Math.round(duration * 60))}, {format(date, "EEEE, MMM d")} · {tzLabel(tz)}
          </p>
          {dayClosed ? (
            <div className="mt-3 rounded-xl p-3 text-sm flex items-center gap-2" style={{ background: "rgba(255,46,151,0.12)", border: "1px solid rgba(255,46,151,0.4)", color: "#ff8ac4" }} data-testid="closed-banner">
              <Clock className="w-4 h-4" /> {closure ? `Closed — ${closure.name}` : "Closed on this day"}. Please choose another date.
            </div>
          ) : (
            <p className="text-slate-500 text-xs mt-2" data-testid="hours-banner">Open {fmtSlot(openM)} – {fmtSlot(closeM)}</p>
          )}
        </SectionCard>

        {/* Stations */}
        <SectionCard>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
              <Gamepad2 className="w-5 h-5" style={{ color: "#ff2e97" }} /> Select Stations
            </h3>
            {loadingAvail && <span className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Checking availability</span>}
          </div>
          {partyConfig?.enabled && (
            <label className="flex items-center gap-4 p-5 mb-5 rounded-2xl cursor-pointer transition-all" style={{ background: partyBooking ? "rgba(255,46,151,0.16)" : "rgba(8,11,22,0.7)", border: `2px solid ${partyBooking ? "rgba(255,46,151,0.65)" : "rgba(120,150,220,0.18)"}` }} data-testid="party-booking-row">
              <input type="checkbox" className="w-6 h-6 shrink-0" style={{ accentColor: "#ff2e97" }} checked={partyBooking} onChange={(e) => setPartyBooking(e.target.checked)} data-testid="party-booking-toggle" />
              <div className="flex-1">
                <p className="text-white font-display font-bold uppercase tracking-wider text-base flex items-center gap-2">
                  <Sparkles className="w-5 h-5" style={{ color: "#ff2e97" }} /> Book the whole lounge (party mode)
                </p>
                <p className="text-slate-300 text-sm mt-1.5">
                  Locks all stations to your booking at a flat <span className="font-semibold text-white">${Number(partyConfig.hourly_rate).toFixed(0)}/hr</span>. Full-hour increments · minimum {partyConfig.min_hours}h · up to 8 hours.
                </p>
              </div>
            </label>
          )}
          {multiUnitTiers && multiUnitTiers.length > 0 && !partyBooking && (
            <div className="rounded-xl p-3.5 mb-3" style={{ background: "rgba(0,255,157,0.08)", border: "1px solid rgba(0,255,157,0.35)" }} data-testid="multi-unit-tips">
              <p className="text-sm font-semibold flex items-center gap-2" style={{ color: "#00ff9d" }}>
                <DollarSign className="w-4 h-4" /> Book more, save more
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {multiUnitTiers.map((t, i) => {
                  const active = selectedStations.length >= Number(t.min_stations);
                  return (
                    <span key={i} className="text-xs px-2.5 py-1 rounded-full font-semibold"
                      style={active
                        ? { background: "rgba(0,255,157,0.2)", border: "1px solid rgba(0,255,157,0.6)", color: "#fff" }
                        : { border: "1px solid rgba(120,150,220,0.2)", color: "#94a3b8" }}>
                      {t.min_stations}+ stations → {t.discount_percentage}% off
                    </span>
                  );
                })}
              </div>
              <p className="text-slate-400 text-xs mt-2">Discount is applied automatically at checkout.</p>
            </div>
          )}
          {!partyBooking && (
            <p className="text-slate-400 text-xs mb-3 flex items-center gap-1.5" data-testid="station-select-hint">
              👆 Tap a station to select it; tap again to unselect. Select multiple to book several at once.
            </p>
          )}
          {sharedGroupsSelected.length > 0 && (
            <div className="rounded-xl p-3.5 mb-3" style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.35)" }} data-testid="shared-asset-note">
              <p className="text-sm font-semibold flex items-center gap-2" style={{ color: "#00e5ff" }}>
                <Sparkles className="w-4 h-4" /> Shared stations in this booking
              </p>
              <p className="text-slate-300 text-xs mt-1.5">
                These stations share components, so only one can be used at a time — but your group can freely switch between them during the booking:
              </p>
              <ul className="mt-1.5 space-y-1">
                {sharedGroupsSelected.map((g, i) => (
                  <li key={i} className="text-xs text-white font-semibold">• {g.join(" ↔ ")}</li>
                ))}
              </ul>
            </div>
          )}
          {stations.length === 0 ? <InlineLoader label="Loading stations" /> : (
            <div className="grid sm:grid-cols-2 gap-3">
              {stations.map((s) => {
                const isAvail = availableIds.has(s.id);
                const isSel = selected.includes(s.id);
                const partner = !isSel ? stations.find((o) => o.id !== s.id && selected.includes(o.id) && ((o.shared_with || []).includes(s.id) || (s.shared_with || []).includes(o.id))) : null;
                const sharedBlocked = effectiveBlockedIds.has(s.id);
                const disabled = !isAvail || sharedBlocked;
                const canMotion = isSel && s.motion_available;
                const motionOn = canMotion && motionByStation.has(s.id);
                const imgSrc = s.image_url ? (/^https?:\/\//i.test(s.image_url) ? s.image_url : `${process.env.REACT_APP_BACKEND_URL || ""}${s.image_url}`) : null;
                return (
                  <div key={s.id}
                    className="rounded-xl p-4 transition-all relative group"
                    style={{
                      background: isSel ? "rgba(0,229,255,0.1)" : "rgba(8,11,22,0.6)",
                      border: `1px solid ${isSel ? "rgba(0,229,255,0.6)" : "rgba(120,150,220,0.14)"}`,
                      opacity: disabled ? 0.4 : 1,
                    }}>
                    {sharedBlocked && (
                      <div className="absolute z-30 left-2 right-2 bottom-full mb-1 hidden group-hover:block pointer-events-none" data-testid={`shared-balloon-${s.id}`}>
                        <div className="rounded-lg px-3 py-2 text-xs text-white shadow-xl" style={{ background: "rgba(15,17,25,0.98)", border: "1px solid rgba(255,46,151,0.5)" }}>
                          This station shares components with {partner.name} and can&apos;t be booked together.
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => toggleStation(s.id)} disabled={disabled}
                      data-testid={`station-${s.id}`}
                      className="w-full text-left"
                      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
                    >
                      {imgSrc && (
                        <div className="rounded-lg overflow-hidden mb-3 border border-white/10" style={{ aspectRatio: "16/9" }}>
                          <img src={imgSrc} alt={s.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-display font-bold text-white">{imgSrc ? "" : (STATION_ICONS[s.type] || "🎮") + " "}{s.name}</p>
                          <p className="text-slate-400 text-xs mt-1 capitalize">{s.type?.replace(/_/g, " ")}</p>
                        </div>
                        {isSel && <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "#00e5ff" }}><Check className="w-4 h-4 text-black" /></div>}
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <span className="font-display text-lg neon-cyan-text">${s.hourly_rate}/hr</span>
                        {!isAvail && <span className="chip" style={{ background: "rgba(255,46,151,0.12)", border: "1px solid rgba(255,46,151,0.4)", color: "#ff2e97" }}>Booked</span>}
                        {isAvail && sharedBlocked && <span className="chip" style={{ background: "rgba(255,46,151,0.12)", border: "1px solid rgba(255,46,151,0.4)", color: "#ff2e97" }}>Shares hardware</span>}
                      </div>
                    </button>
                    {canMotion && (
                      <button
                        onClick={() => toggleMotion(s.id)}
                        data-testid={`motion-toggle-${s.id}`}
                        className="mt-3 w-full flex items-center justify-between rounded-lg px-3 py-2 transition-all"
                        style={{
                          background: motionOn ? "rgba(139,92,246,0.15)" : "rgba(8,11,22,0.5)",
                          border: `1px solid ${motionOn ? "rgba(139,92,246,0.6)" : "rgba(139,92,246,0.25)"}`,
                        }}
                      >
                        <span className="flex items-center gap-2 text-left">
                          <Sparkles className="w-4 h-4 shrink-0" style={{ color: "#c4b5fd" }} />
                          <span className="text-white text-xs font-bold">
                            Add motion
                            <span className="block text-slate-400 text-[10px] font-normal">
                              +${(Number(s.motion_price || 0) * duration).toFixed(2)}
                            </span>
                          </span>
                        </span>
                        <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: motionOn ? "#8b5cf6" : "transparent", border: motionOn ? "none" : "1px solid rgba(139,92,246,0.5)" }}>
                          {motionOn && <Check className="w-3 h-3 text-white" />}
                        </span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        {!isGuest && user?.free_session_credits > 0 && (
          <SectionCard>
            <button onClick={() => setUseFreeCredit(!useFreeCredit)}
              className="w-full flex items-center justify-between rounded-xl p-4 transition-all"
              style={{ background: useFreeCredit ? "rgba(0,255,157,0.1)" : "rgba(8,11,22,0.6)", border: `1px solid ${useFreeCredit ? "rgba(0,255,157,0.6)" : "rgba(120,150,220,0.14)"}` }}
              data-testid="use-free-credit">
              <span className="flex items-center gap-3 text-white font-semibold">
                <Star className="w-5 h-5" style={{ color: "#00ff9d" }} /> Use a Free Gaming Hour ({user.free_session_credits} available)
              </span>
              <span className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: useFreeCredit ? "#00ff9d" : "transparent", border: useFreeCredit ? "none" : "1px solid rgba(120,150,220,0.4)" }}>
                {useFreeCredit && <Check className="w-4 h-4 text-black" />}
              </span>
            </button>
          </SectionCard>
        )}
      </div>

      {/* Summary */}
      <div className="lg:col-span-1">
        <div className="glass rounded-2xl p-6 lg:sticky lg:top-24 space-y-5" data-testid="booking-summary">
          <div>
            <h3 className="font-display text-lg font-bold text-white mb-4">Order Summary</h3>
            <div className="space-y-2.5 text-sm">
              <Row label={partyBooking ? `Whole lounge × ${duration}h` : `${selectedStations.length} station(s) × ${duration}h`} value={`$${stationsAmount.toFixed(2)}`} />
              {motionAmount > 0 && <Row label={`Motion (${activeMotionIds.length} station${activeMotionIds.length > 1 ? "s" : ""})`} value={`+$${motionAmount.toFixed(2)}`} accent="#c4b5fd" />}
              {multiUnitPct > 0 && <Row label={`Multi-station -${multiUnitPct}%`} value={`-$${multiUnitDiscount.toFixed(2)}`} accent="#00ff9d" />}
              {!isGuest && membershipDiscount > 0 && <Row label={`Membership -${membershipDiscount}%`} value={`-$${(baseAmount - afterMembership).toFixed(2)}`} accent="#8b5cf6" />}
              {!isGuest && pointsToUse > 0 && <Row label={`Points (${pointsToUse})`} value={`-$${pointsDiscount.toFixed(2)}`} accent="#ffb020" />}
              {!isGuest && freeCreditValue > 0 && <Row label="Free Gaming Hour" value={`-$${freeCreditValue.toFixed(2)}`} accent="#00ff9d" />}
              {taxAmount > 0 && <Row label="Subtotal" value={`$${estTotal.toFixed(2)}`} />}
              {taxAmount > 0 && <Row label={`${taxSettings.label || "HST"} (${taxRate}%)`} value={`+$${taxAmount.toFixed(2)}`} accent="#94a3b8" />}
            </div>
            <div className="border-t border-white/10 mt-4 pt-4 flex items-center justify-between">
              <span className="text-slate-300 uppercase tracking-widest text-xs font-bold">{isGuest ? "Total" : "Est. Total"}</span>
              <span className="font-display text-3xl font-black neon-cyan-text" data-testid="est-total">${grandTotal.toFixed(2)}</span>
            </div>
          </div>

          <div className="border-t border-white/10 pt-4 space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> Party Size *</label>
              <input type="number" min={1} max={20} className="input-cyber" value={partySize}
                onChange={(e) => setPartySize(Math.max(1, Number(e.target.value)))} data-testid="party-size" />
              <p className="text-slate-500 text-xs mt-1.5" data-testid="party-size-note">Doesn&apos;t change the stations booked — it just helps us prep for your group size.</p>
            </div>
            {!isGuest && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2"><Zap className="w-4 h-4" /> Points to Use <span className="text-slate-500 normal-case tracking-normal font-normal">({user?.points?.toLocaleString() || 0} avail · 100 = $1)</span></label>
                <input type="number" min={0} max={user?.points || 0} step={100} className="input-cyber" value={pointsToUse}
                  onChange={(e) => setPointsToUse(Math.min(user?.points || 0, Math.max(0, Number(e.target.value))))} data-testid="points-to-use" />
              </div>
            )}
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Special Requests</label>
              <textarea className="input-cyber min-h-[70px] resize-none" placeholder="Any notes for our team" value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)} data-testid="special-requests" />
            </div>
          </div>

          {isGuest && baseAmount > 0 && (
            <div className="rounded-xl p-3.5" style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.35)" }} data-testid="guest-benefits">
              <p className="text-sm text-slate-200 flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#c4b5fd" }} />
                <span>With an account you&apos;d earn <b style={{ color: "#ffb020" }}>~{potentialPoints} points</b> on this booking and unlock member discounts on your next visit.</span>
              </p>
            </div>
          )}

          <div>
            <p className="text-slate-500 text-xs mb-2">All prices in CAD. Final total confirmed at checkout.</p>
            <button onClick={handleSubmit} disabled={submitting || selected.length === 0 || dayClosed || isUnavailable(startMinutes)}
              className="neon-btn w-full py-4" data-testid="confirm-booking">
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</> : dayClosed ? "Closed — Pick Another Day" : estTotal > 0 ? "Proceed to Payment" : "Confirm Booking"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
